import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import moment from "moment";
import BasicButton from "components/forms/BasicButton";
import ProjectFileDrop from "components/ProjectFileDrop";
import { flushClientInput } from "api/Projects";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { themevals } from "theme/themevals";
import { discountBase, earlyPayFigures, EARLY_PAY, linesTotal } from "helpers/earlyPay";

/**
 * ONE COMPACT "QUOTE ACCEPTED" WINDOW (2026-10-12, Jack: the old one was a
 * full-width table for three numbers). Used by every accept entry point
 * that stays on the page (quote Overview, dashboard Quotes Awaiting
 * Response). Says what happened, the ONE next step that fits this quote's
 * workflow, and asks for the PO / site maps while the client is here:
 *   - early-pay savings  -> two savings options + Pay now
 *   - deposit required   -> the deposit amount + Pay deposit
 *   - price-locked       -> deposit received, balance due
 *   - otherwise          -> invoice ready
 * Staff accepting on a client's behalf get Send invoice to billing.
 * 2026-10-12 pass 2 (Jack testing): View project in the footer (lands on
 * Files after an upload), payment terms / due date, rush and under-minimum
 * notes so a missing saving is explained, PO number field, no Close button
 * (the X and backdrop close it).
 *
 * Eligibility mirrors Quotes/components/Overview (minimum, rush, deposit
 * client, per-quote switch) so both entry points say the same thing.
 */

const RUSH_THRESHOLD_DAYS = 5;

export const acceptedWorkflow = (quote: any) => {
  const q = quote || {};
  const total = linesTotal(q.items);
  const isRush = (() => {
    if (q.project?.isRushOrder !== undefined && q.project?.isRushOrder !== null) return q.project.isRushOrder === true;
    if (q.serviceDate) return moment(q.serviceDate).diff(moment().startOf("day"), "days") <= RUSH_THRESHOLD_DAYS;
    return false;
  })();
  const requiresDeposit = !!q.requiresDeposit || q.client?.requiresDeposit === true || q.primaryClient?.requiresDeposit === true;
  const depositPercent = (() => {
    const raw = Number((q.requiresDeposit ? q.depositPercentage : null) ?? q.client?.depositPercentage ?? q.primaryClient?.depositPercentage ?? 50);
    return raw > 0 && raw < 100 ? raw : 50;
  })();
  const lockedDeposit = Number(q.priceLockDepositAmount || 0);
  const locked = Number(q.status) === 6 || lockedDeposit > 0;
  const earlyPay = total >= EARLY_PAY.minTotal && !isRush && !requiresDeposit && !q.earlyPaymentDiscountOff;
  const depositNow = !earlyPay && requiresDeposit && !locked;
  // Why there are no savings, when it's worth saying: a rush order, or an
  // order under the minimum (tells them the offer exists for next time).
  const offerApplies = !requiresDeposit && !q.earlyPaymentDiscountOff && !locked;
  const savingsNote = earlyPay || !offerApplies ? null : isRush ? "rush" : total < EARLY_PAY.minTotal ? "minimum" : null;
  return { total, isRush, earlyPay, depositNow, locked, lockedDeposit, depositPercent, savingsNote, depositAmount: Math.round(total * depositPercent) / 100 };
};

interface Props {
  quote: any;
  /** Staff accepting on the client's behalf. */
  isStaff?: boolean;
  /** Overrides when the caller knows the new project before the quote reloads. */
  projectId?: string | null;
  projectSequence?: number | null;
  onClose: () => void;
  onPay: () => void;
  onSendToBilling?: () => void;
  sendingToBilling?: boolean;
}

const money = (n: number) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const QuoteAcceptedModal: React.FC<Props> = ({ quote, isStaff, projectId, projectSequence, onClose: closeModal, onPay: payInvoice, onSendToBilling, sendingToBilling }) => {
  // Leaving this window ends the client's "visit": anything they added
  // here goes to the team as one Slack digest (2026-10-12).
  const flush = () => {
    if (!isStaff) flushClientInput(projectId ?? quote?.project?.id);
  };
  const onClose = () => {
    flush();
    closeModal();
  };
  const onPay = () => {
    flush();
    payInvoice();
  };
  const wf = acceptedWorkflow(quote);
  const epf = earlyPayFigures(wf.total, discountBase(quote?.items));
  const seq = projectSequence ?? quote?.project?.sequence ?? null;
  const gmp = seq ? `Project GMP-${String(seq).padStart(5, "0")}` : null;
  const pid = projectId ?? quote?.project?.id ?? null;
  const navigate = useNavigate();
  const [uploaded, setUploaded] = useState(false);
  const dueDate = quote?.invoice?.dueDate ? moment.utc(quote.invoice.dueDate).format("MMM D, YYYY") : null;
  const terms = dueDate ? `due ${dueDate}` : "on standard terms (Net 30)";
  const viewProject = () => {
    if (!pid) return;
    onClose();
    navigate("/projects/" + pid + (uploaded ? "?tab=files" : ""));
  };
  const deadline = moment(quote?.acceptedDate || new Date()).add(EARLY_PAY.windowDays, "days").format("ddd, MMM D");

  // Each saving is its own pay button (Jack, 2026-10-12: the cards read as
  // something to click). Opens the invoice's pay chooser on that option.
  const invoiceId = quote?.invoice?.id ?? null;
  const payOption = (opt: "full" | "deposit") => {
    if (!invoiceId) return onPay();
    onClose();
    navigate("/invoices/" + invoiceId + "?pay=" + opt);
  };
  const option = (opt: "full" | "deposit", label: string, payNow: number, after: string | null, save: number, pct: number) => (
    <Flex sx={{ flex: "1 1 220px", flexDirection: "column", gap: "2px", border: "1px solid #CFE3B4", backgroundColor: "#F3F8EC", borderRadius: "8px", padding: "10px 12px" }}>
      <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey }}>{label}</Box>
      <Box sx={{ fontSize: "17px", fontWeight: 700, color: "#111" }}>
        {money(payNow)}
        <Box as="span" sx={{ fontSize: "12px", fontWeight: 400, color: themevals.colors.dkgrey }}>{" now"}</Box>
      </Box>
      <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, minHeight: "18px" }}>{after || "Nothing more to pay"}</Box>
      <Box sx={{ fontSize: "12px", fontWeight: 600, color: "#27500A", marginBottom: "8px" }}>{`Save ${money(save)} (${pct}%)`}</Box>
      {!isStaff && (
        <BasicButton type="button" scheme="primary" onClick={() => payOption(opt)} keepText={true} styles={{ width: "100%", marginTop: "auto" }}>
          {`Pay ${money(payNow)}`}
        </BasicButton>
      )}
    </Flex>
  );

  return (
    <Modal isOpen={true} toggle={onClose} size={"md"} centered={true}>
      <ModalHeader toggle={onClose}>
        <ModalTitleWrapper>
          <ModalTitle>Quote accepted</ModalTitle>
          <ModalSubtitle>
            {isStaff
              ? "Accepted on the client's behalf." + (gmp ? ` ${gmp} is created.` : "")
              : (gmp ? `${gmp} is created. ` : "Your project is created. ") + "We'll be in touch to schedule it."}
          </ModalSubtitle>
        </ModalTitleWrapper>
      </ModalHeader>
      <ModalBody>
        <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
          {wf.earlyPay && (
            <>
              <Box sx={{ fontSize: "14px", fontWeight: 600, color: "#111", marginBottom: "8px" }}>{`Pay by ${deadline} to save`}</Box>
              <Flex sx={{ gap: "10px", flexWrap: "wrap" }}>
                {option("full", "Pay in full", epf.fullTotal, null, epf.fullSave, epf.fullPct)}
                {option("deposit", "Pay a 50% deposit", epf.depositNow, `then ${money(Math.round((epf.depositTotal - epf.depositNow) * 100) / 100)} later`, epf.depositSave, epf.depositPct)}
              </Flex>
              <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "8px" }}>
                {`Or pay ${money(wf.total)} ${terms}. Savings come off when your payment arrives.`}
              </Box>
            </>
          )}
          {wf.depositNow && (
            <Box sx={{ fontSize: "14px", color: "#111" }}>
              {`A ${wf.depositPercent}% deposit of `}<strong>{money(wf.depositAmount)}</strong>{" schedules this work. Pay it from your invoice."}
            </Box>
          )}
          {wf.locked && (
            <Box sx={{ fontSize: "14px", color: "#111" }}>
              {"Your deposit of "}<strong>{money(wf.lockedDeposit)}</strong>{" is applied. Balance due: "}
              <strong>{money(wf.total - wf.lockedDeposit)}</strong>.
            </Box>
          )}
          {!wf.earlyPay && !wf.depositNow && !wf.locked && (
            <Box sx={{ fontSize: "14px", color: "#111" }}>{`Your invoice for ${money(wf.total)} is ready, ${terms}.`}</Box>
          )}
          {wf.isRush && (
            <Box sx={{ fontSize: "12px", color: "#8A5300", marginTop: "6px" }}>
              Rush order: we&apos;ll prioritize scheduling.{wf.savingsNote === "rush" ? " Early-payment savings don't apply to rush orders." : ""}
            </Box>
          )}
          {wf.savingsNote === "minimum" && (
            <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "6px" }}>
              {`Tip: orders of ${money(EARLY_PAY.minTotal)} or more save up to ${EARLY_PAY.fullPct}% when paid within ${EARLY_PAY.windowDays} days of accepting.`}
            </Box>
          )}
          <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "6px" }}>
            {"Invoice goes to "}<strong>{quote?.primaryClient?.billingEmail || quote?.primaryClient?.contactEmail || "your billing contact"}</strong>
          </Box>
          <ProjectFileDrop
            projectId={pid}
            variant="accepted"
            clientPO={quote?.project?.clientPO}
            neededBy={quote?.project?.clientNeededBy}
            rfqDate={quote?.quoteRequest?.serviceDate || quote?.serviceDate}
            onUploaded={() => setUploaded(true)}
          />
        </Box>
        {/* ModalBody drops its bottom padding when it holds an input; restore it. */}
        <Box sx={{ height: "20px" }} />
      </ModalBody>
      <ModalFooter>
        <Flex sx={{ gap: "10px", justifyContent: "flex-end", flexWrap: "wrap", width: "100%" }}>
          {pid && (
            <BasicButton type="button" scheme="secondary" outline={true} onClick={viewProject} keepText={true}>
              View project
            </BasicButton>
          )}
          {isStaff && onSendToBilling && (
            <BasicButton type="button" scheme="secondary" outline={true} onClick={onSendToBilling} $submitting={!!sendingToBilling} keepText={true} $loadingColor="#000">
              Send invoice to billing
            </BasicButton>
          )}
          {isStaff || wf.earlyPay ? (
            <BasicButton type="button" scheme="secondary" outline={true} onClick={onPay} keepText={true}>View invoice</BasicButton>
          ) : (
            <BasicButton type="button" scheme="primary" onClick={onPay} keepText={true}>
              {wf.depositNow ? "Pay deposit" : "View invoice"}
            </BasicButton>
          )}
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default QuoteAcceptedModal;
